import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  Button,
  Avatar,
  Typography,
  Switch,
  Tag,
  Input,
  Modal,
  Descriptions,
  Spin,
} from "antd";
import {
  DollarOutlined,
  UserOutlined,
  PhoneOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  SearchOutlined,
  WalletOutlined,
  BankOutlined,
  CreditCardOutlined,
  NumberOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";
import moment from "moment";
import {
  useGetRedeemTransactionsQuery,
  useCompleteRedeemTransactionMutation,
  useLazyGetBankAccountByConsultantIdQuery,
} from "../../redux/rtk/apiRtk";
import EnhancedConfirmationAlert from "../../components/alert/EnhancedConfirmationAlert";

const { Title, Text } = Typography;

const ConsultantCoinRedeem = () => {
  // Theme color
  const primaryColor = "#A40165";

  // State
  const [status, setStatus] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [animateTable, setAnimateTable] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const userId = useRef();

  // Debug state changes
  useEffect(() => {
    console.log("isModalVisible changed:", isModalVisible);
  }, [isModalVisible]);

  useEffect(() => {
    console.log("selectedConsultant changed:", selectedConsultant);
  }, [selectedConsultant]);

  // RTK Query hooks
  const { data: listData, isLoading: loading } = useGetRedeemTransactionsQuery({
    status,
    page: currentPage,
    limit: 10,
  });
  const [getBankDetails, { data: bank, isLoading: bankLoading }] =
    useLazyGetBankAccountByConsultantIdQuery();
  const [completeRedeemTransaction, { isLoading: isSubmitting }] =
    useCompleteRedeemTransactionMutation();

  // Animation effect
  useEffect(() => {
    setTimeout(() => {
      setAnimateTable(true);
    }, 100);
  }, []);

  // Handle redeem completion
  const completeRedeem = async () => {
    try {
      await completeRedeemTransaction(userId.current).unwrap();
      toast.success("Payment Successful");
    } catch (error) {
      console.log(error);
      toast.error(error?.data?.message || "Payment failed");
    }
  };

  const handleConfirm = () => {
    completeRedeem();
    setShowAlert(false);
  };

  const handleCancel = () => {
    setShowAlert(false);
  };

  // Search functionality
  const handleSearch = (value) => {
    setSearchText(value);
  };

  // Table columns
  const columns = [
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <UserOutlined style={{ color: primaryColor }} />
          </div>
          <span>CONSULTANT</span>
        </div>
      ),
      dataIndex: "name",
      key: "name",
      width: "25%",
      render: (_, record) => (
        <div className="flex items-center py-1">
          {record.image ? (
            <Avatar
              size={44}
              src={record.image}
              className="border-2 border-white shadow-sm"
              style={{ boxShadow: `0 0 0 2px ${primaryColor}30` }}
            />
          ) : (
            <Avatar
              size={44}
              icon={<UserOutlined />}
              className="border-2 border-white shadow-sm"
              style={{
                backgroundColor: `${primaryColor}50`,
                color: primaryColor,
              }}
            />
          )}
          <div className="ml-3">
            <Text
              strong
              className="block text-gray-800"
              style={{ fontSize: "14px", lineHeight: "1.4" }}
            >
              {record.firstName} {record.lastName}
            </Text>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Consultant
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <PhoneOutlined style={{ color: primaryColor }} />
          </div>
          <span>PHONE</span>
        </div>
      ),
      dataIndex: "phone",
      key: "phone",
      width: "15%",
      render: (phone) => (
        <div className="flex items-center py-1">
          <div
            className="flex items-center justify-center w-8 h-8 rounded-full mr-3"
            style={{ background: `${primaryColor}10` }}
          >
            <PhoneOutlined style={{ color: primaryColor, fontSize: "14px" }} />
          </div>
          <Text style={{ fontSize: "14px" }}>{phone}</Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <DollarOutlined style={{ color: primaryColor }} />
          </div>
          <span>AMOUNT</span>
        </div>
      ),
      key: "amount",
      dataIndex: "amount",
      width: "15%",
      render: (amount) => (
        <div className="py-1">
          <Tag
            className="text-sm px-4 py-1 font-medium"
            style={{
              backgroundColor: `${primaryColor}15`,
              color: primaryColor,
              border: `1px solid ${primaryColor}30`,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              height: "32px",
            }}
          >
            <DollarOutlined style={{ fontSize: "14px" }} />
            <span style={{ fontSize: "15px", fontWeight: 500 }}>₹{amount}</span>
          </Tag>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <span className="h-2 w-2 rounded-full bg-green-500"></span>
          </div>
          <span>STATUS</span>
        </div>
      ),
      key: "status",
      dataIndex: "status",
      width: "20%",
      render: (_, record) => (
        <div className="py-1">
          <Button
            type="primary"
            disabled={record.status}
            onClick={async () => {
              console.log("Button clicked, record:", record);
              setSelectedConsultant({
                id: record.id,
                firstName: record.firstName,
                lastName: record.lastName,
                amount: record.amount,
                consultantId: record.consultantId,
              });
              userId.current = record.id;
              console.log(
                "About to fetch bank details for consultantId:",
                record.consultantId
              );
              try {
                await getBankDetails(record.consultantId);
                console.log("Bank details fetched, setting modal visible");
                setIsModalVisible(true);
              } catch (error) {
                console.error("Error fetching bank details:", error);
                setIsModalVisible(true); // Show modal even if bank details fail
              }
            }}
            style={{
              backgroundColor: record.status ? "#8c8c8c" : primaryColor,
              borderColor: record.status ? "#8c8c8c" : primaryColor,
              boxShadow: record.status
                ? "none"
                : `0 2px 8px 0 ${primaryColor}40`,
              height: "36px",
              minWidth: "140px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 16px",
            }}
          >
            {record.status ? (
              <>
                <CheckCircleFilled style={{ marginRight: "8px" }} />
                Redeemed
              </>
            ) : (
              <>
                <WalletOutlined style={{ marginRight: "8px" }} />
                Redeem Money
              </>
            )}
          </Button>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <CalendarOutlined style={{ color: primaryColor }} />
          </div>
          <span>DATE</span>
        </div>
      ),
      key: "date",
      dataIndex: "date",
      width: "20%",
      render: (date) => (
        <div className="py-1">
          <div className="flex items-center mb-1">
            <CalendarOutlined
              style={{
                fontSize: "14px",
                color: primaryColor,
                marginRight: "8px",
              }}
            />
            <Text style={{ fontSize: "14px" }}>
              {moment(date).format("MMM Do, YYYY")}
            </Text>
          </div>
          <Text
            type="secondary"
            style={{ fontSize: "12px", marginLeft: "22px" }}
          >
            {moment(date).format("h:mm A")}
          </Text>
        </div>
      ),
    },
  ];

  // Format data for table
  const dataSource =
    listData?.results?.map((item) => ({
      key: item._id,
      id: item._id,
      consultantId: item?.consultantId?._id,
      firstName: item?.consultantId?.firstName,
      lastName: item?.consultantId?.lastName,
      image: item?.consultantId?.image,
      phone: item?.consultantId?.phone,
      amount: item?.amount,
      status: item?.status,
      date: item?.createdAt,
    })) || [];

  return (
    <>
      <EnhancedConfirmationAlert
        visible={showAlert}
        message={"Have you made payment to this consultant?"}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        loading={isSubmitting}
        bankDetails={bank}
        consultantName={
          selectedConsultant
            ? `${selectedConsultant.firstName} ${selectedConsultant.lastName}`
            : ""
        }
        amount={selectedConsultant?.amount}
        primaryColor={primaryColor}
      />

      <ToastContainer />

      {/* Bank Details Modal */}
      <Modal
        title={
          <div className="flex items-center">
            <BankOutlined
              style={{
                fontSize: "20px",
                color: primaryColor,
                marginRight: "12px",
              }}
            />
            <span style={{ color: primaryColor, fontWeight: 500 }}>
              Bank Account Details
            </span>
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          console.log("Modal cancel clicked");
          setIsModalVisible(false);
        }}
        footer={[
          <Button key="cancel" onClick={() => setIsModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={isSubmitting}
            onClick={() => {
              setIsModalVisible(false);
              setTimeout(() => {
                setShowAlert(true);
              }, 300);
            }}
            style={{
              backgroundColor: primaryColor,
              borderColor: primaryColor,
            }}
          >
            Confirm Payment
          </Button>,
        ]}
        width={600}
        centered
        bodyStyle={{ padding: "24px" }}
      >
        {bankLoading ? (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" tip="Loading bank details..." />
          </div>
        ) : bank ? (
          <div>
            <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-100">
              <div className="flex items-center mb-3">
                <UserOutlined
                  style={{
                    color: primaryColor,
                    fontSize: "16px",
                    marginRight: "8px",
                  }}
                />
                <Text strong style={{ fontSize: "16px" }}>
                  {selectedConsultant?.firstName} {selectedConsultant?.lastName}
                </Text>
              </div>
              <div className="flex items-center">
                <DollarOutlined
                  style={{
                    color: primaryColor,
                    fontSize: "16px",
                    marginRight: "8px",
                  }}
                />
                <Text>
                  Payment Amount:{" "}
                  <Text strong>₹{selectedConsultant?.amount}</Text>
                </Text>
              </div>
            </div>

            <Descriptions
              title="Bank Account Information"
              bordered
              column={1}
              labelStyle={{ fontWeight: 500, width: "180px" }}
              contentStyle={{ fontWeight: 400 }}
            >
              <Descriptions.Item
                label={
                  <div className="flex items-center">
                    <IdcardOutlined
                      style={{ marginRight: "8px", color: primaryColor }}
                    />
                    Account Holder
                  </div>
                }
              >
                {bank.accountHolderName}
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <div className="flex items-center">
                    <BankOutlined
                      style={{ marginRight: "8px", color: primaryColor }}
                    />
                    Bank Name
                  </div>
                }
              >
                {bank.bankName}
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <div className="flex items-center">
                    <CreditCardOutlined
                      style={{ marginRight: "8px", color: primaryColor }}
                    />
                    Account Number
                  </div>
                }
              >
                {bank.accountNumber}
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <div className="flex items-center">
                    <NumberOutlined
                      style={{ marginRight: "8px", color: primaryColor }}
                    />
                    IFSC Code
                  </div>
                }
              >
                {bank.ifscCode}
              </Descriptions.Item>
            </Descriptions>

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-100 rounded-lg">
              <Text type="warning">
                Please verify all details before making the payment. Once
                confirmed, the status will be updated to "Redeemed".
              </Text>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Text type="danger" style={{ fontSize: "16px" }}>
              No bank account details found for this consultant.
            </Text>
            <div className="mt-4">
              <Text type="secondary">
                Please ask the consultant to add their bank details before
                processing the payment.
              </Text>
            </div>
          </div>
        )}
      </Modal>

      <ToastContainer />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{
          opacity: animateTable ? 1 : 0,
          y: animateTable ? 0 : 20,
        }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl shadow-lg overflow-hidden"
        style={{ boxShadow: "0 10px 40px rgba(164, 1, 101, 0.1)" }}
      >
        {/* Header */}
        <div
          className="py-6 px-6 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6"
          style={{ borderColor: "rgba(164, 1, 101, 0.1)" }}
        >
          <div className="flex items-center">
            <div
              className="flex items-center justify-center w-12 h-12 rounded-full mr-4"
              style={{ background: `${primaryColor}15` }}
            >
              <WalletOutlined style={{ fontSize: 22, color: primaryColor }} />
            </div>
            <div>
              <Title
                level={4}
                style={{ margin: 0, marginBottom: "4px", color: primaryColor }}
              >
                Coin Redemption Management
              </Title>
              <Text type="secondary" style={{ fontSize: "14px" }}>
                {listData?.total || 0} redemption requests found
              </Text>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
            <div
              className="custom-search-wrapper w-full sm:w-auto"
              style={{ minWidth: "250px" }}
            >
              <Input
                placeholder="Search by name or phone..."
                prefix={
                  <SearchOutlined
                    style={{ color: "#8c8c8c", fontSize: "16px" }}
                  />
                }
                allowClear
                onChange={(e) => handleSearch(e.target.value)}
                className="custom-search-input"
                style={{
                  borderRadius: "8px",
                  height: "40px",
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
                }}
              />
            </div>

            <div className="flex items-center justify-center gap-3 bg-gray-50 px-4 py-2 rounded-lg border border-gray-100 w-full sm:w-auto">
              <Text
                type="secondary"
                style={{ whiteSpace: "nowrap", fontSize: "14px" }}
              >
                Show Completed:
              </Text>
              <Switch
                checked={status}
                onChange={() => setStatus(!status)}
                style={{ backgroundColor: status ? primaryColor : undefined }}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="p-0">
          <Table
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            pagination={{
              pageSize: 10,
              total: listData?.total || 0,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} requests`,
              onChange: (page) => setCurrentPage(page),
              className: "px-6 py-4",
            }}
            className="custom-table"
            rowClassName={() => "table-row"}
            locale={{
              emptyText: (
                <div className="py-8 text-center">
                  <WalletOutlined style={{ fontSize: 32, color: "#ccc" }} />
                  <p className="mt-2 text-gray-500">
                    {status
                      ? "No completed redemptions found"
                      : "No pending redemption requests"}
                  </p>
                </div>
              ),
            }}
          />
        </div>
      </motion.div>

      {/* Custom styles */}
      <style jsx="true">{`
        .custom-search-wrapper {
          display: flex;
          align-items: center;
        }

        .custom-search-input {
          border-color: #e2e8f0;
          width: 100%;
        }

        .custom-search-input input {
          height: 24px;
          font-size: 14px;
        }

        .custom-search-input:hover {
          border-color: ${primaryColor}80 !important;
        }

        .custom-search-input:focus,
        .custom-search-input-focused {
          border-color: ${primaryColor} !important;
          box-shadow: 0 0 0 2px ${primaryColor}20 !important;
        }

        .custom-table .ant-table-thead > tr > th {
          background-color: #f9f9f9;
          font-weight: 600;
          color: #333;
          padding: 16px;
        }

        .table-row:hover {
          background-color: ${primaryColor}05 !important;
        }

        .ant-table-row td {
          padding: 12px 16px !important;
          vertical-align: middle !important;
        }

        .ant-table-row-expand-icon {
          color: ${primaryColor} !important;
        }

        .ant-pagination-item-active {
          border-color: ${primaryColor} !important;
        }

        .ant-pagination-item-active a {
          color: ${primaryColor} !important;
        }

        .ant-select-item-option-selected {
          background-color: ${primaryColor}15 !important;
        }

        .ant-modal-header {
          border-bottom: 1px solid ${primaryColor}20;
          padding: 16px 24px;
        }

        .ant-modal-title {
          font-weight: 500;
          font-size: 18px;
        }

        .ant-descriptions-header {
          margin-bottom: 16px;
        }

        .ant-descriptions-title {
          font-size: 16px;
          font-weight: 500;
          color: #333;
        }

        .ant-descriptions-bordered .ant-descriptions-item-label {
          background-color: #f9f9f9;
        }
      `}</style>
    </>
  );
};

export default ConsultantCoinRedeem;
